import React, { useState } from "react";
import config from "../config";
import { QRCodeSVG } from "qrcode.react";
import { auth } from "../firebase";
import axios from "axios";
import { useNavigate } from "react-router-dom";

// Simplified component for game creation only and game management display
function GameCreationForm({
  // For creation
  onGameCreated,
  pagePath,
  // For management (when gameId is provided, show read-only management view)
  gameId,
  existingGameData,
  playersList = [],
  playersLoading = false,
  playersError = null,
  fetchPlayers,
  setFullGameData,
}) {
  const navigate = useNavigate();

  // Creation form state (only used when gameId is not provided)
  const [description, setDescription] = useState("");
  const [roles, setRoles] = useState([{ name: "", description: "" }]);
  const [rounds, setRounds] = useState(1);
  const [steps, setSteps] = useState(1);
  const [stepTime, setStepTime] = useState(60);
  const [startResources, setStartResources] = useState(10);
  const [extraResources, setExtraResources] = useState(1);
  const [maxStatements, setMaxStatements] = useState(5);
  const [resourceCategoryOption, setResourceCategoryOption] = useState("DIME");
  const [customResourceCategories, setCustomResourceCategories] = useState([
    "",
  ]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [isRolesCollapsed, setIsRolesCollapsed] = useState(false);

  // QR code visibility
  const [showQrCode, setShowQrCode] = useState(false);

  // Player removal state
  const [removingPlayerId, setRemovingPlayerId] = useState(null);
  const [removeError, setRemoveError] = useState(null);

  // Game actions state
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);

  // Submission labeling state
  const [labelingSubmissionId, setLabelingSubmissionId] = useState(null);
  const [labelingError, setLabelingError] = useState(null);
  const [labelingSuccess, setLabelingSuccess] = useState(null);
  const [selectingCategoriesFor, setSelectingCategoriesFor] = useState(null);

  // Qualification tracking
  const [qualificationCompletedForRound, setQualificationCompletedForRound] =
    useState(null);

  // Labeling status tracking
  const [labelingStatus, setLabelingStatus] = useState({
    allLabeled: true,
    unlabeledCount: 0,
    isChecking: false,
  });

  // Extract game state from existingGameData
  const currentGameState = existingGameData?.current_state || "LOBBY";
  const currentRound = existingGameData?.current_round || 0;
  const currentStep = existingGameData?.current_step || 0;
  const allSubmissions = existingGameData?.allSubmissions || [];

  // Determine mode
  const isManagementMode = !!gameId;
  const isGameActive = currentGameState !== "LOBBY";

  // Reset qualification status when round/state changes
  React.useEffect(() => {
    console.log(
      `[Effect] Resetting qualification status. Current State: ${currentGameState}, Current Round: ${currentRound}`
    );
    setQualificationCompletedForRound(null);
  }, [currentRound, currentGameState]);

  // Check labeling status when in ACTIVE state
  const checkLabelingStatus = React.useCallback(async () => {
    if (!gameId || currentGameState !== "ACTIVE") {
      setLabelingStatus({
        allLabeled: true,
        unlabeledCount: 0,
        isChecking: false,
      });
      return;
    }

    setLabelingStatus((prev) => ({ ...prev, isChecking: true }));
    try {
      const token = await auth.currentUser?.getIdToken();
      const response = await axios.get(
        `${config.apiUrl}/api/games/${gameId}/labeling-status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { allLabeled, unlabeledCount } = response.data;
      setLabelingStatus({
        allLabeled,
        unlabeledCount,
        isChecking: false,
      });
    } catch (error) {
      console.error("Error checking labeling status:", error);
      // On error, assume all labeled to avoid blocking
      setLabelingStatus({
        allLabeled: true,
        unlabeledCount: 0,
        isChecking: false,
      });
    }
  }, [gameId, currentGameState]);

  // Check labeling status when in ACTIVE state
  React.useEffect(() => {
    if (currentGameState === "ACTIVE") {
      checkLabelingStatus();
    }
  }, [
    currentGameState,
    currentRound,
    allSubmissions.length,
    checkLabelingStatus,
  ]);

  // Role management functions (creation mode only)
  const handleAddRole = () => {
    setRoles([...roles, { name: "", description: "" }]);
  };

  const handleRoleChange = (index, field, value) => {
    const newRoles = [...roles];
    newRoles[index][field] = value;
    setRoles(newRoles);
  };

  const handleRemoveRole = (index) => {
    const newRoles = roles.filter((_, i) => i !== index);
    if (newRoles.length === 0) {
      setRoles([{ name: "", description: "" }]);
    } else {
      setRoles(newRoles);
    }
  };

  const handleNumberChange = (setter, value, min = 1) => {
    const number = parseInt(value, 10);
    if (!isNaN(number) && number >= min) {
      setter(number);
    } else if (value === "") {
      setter(min);
    }
  };

  // Player removal function
  const handleRemovePlayer = async (playerIdToRemove) => {
    if (!gameId || !playerIdToRemove || removingPlayerId) return;

    console.log(
      `Attempting to remove player ${playerIdToRemove} from game ${gameId}`
    );
    setRemovingPlayerId(playerIdToRemove);
    setRemoveError(null);

    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error("Authentication required.");

      await axios.delete(
        `${config.apiUrl}/api/games/${gameId}/players/${playerIdToRemove}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log(`Player ${playerIdToRemove} removed successfully.`);
      if (fetchPlayers) {
        fetchPlayers(gameId);
      }
    } catch (error) {
      console.error(`Error removing player ${playerIdToRemove}:`, error);
      setRemoveError(
        error.response?.data?.error ||
          error.message ||
          "Failed to remove player."
      );
    } finally {
      setRemovingPlayerId(null);
    }
  };

  // Game creation submission
  const handleSubmit = async (event) => {
    event.preventDefault();

    // Only allow submission in creation mode
    if (isManagementMode) return;

    setIsSubmitting(true);
    setSubmitError(null);

    // Determine final resource categories
    let finalResourceCategories = [];
    if (resourceCategoryOption === "DIME") {
      finalResourceCategories = [
        "Diplomatic",
        "Information",
        "Military",
        "Economic",
      ];
    } else if (resourceCategoryOption === "PMESII") {
      finalResourceCategories = [
        "Political",
        "Military",
        "Economic",
        "Social",
        "Information",
        "Infrastructure",
      ];
    } else if (resourceCategoryOption === "CUSTOM") {
      finalResourceCategories = customResourceCategories
        .map((cat) => cat.trim())
        .filter((cat) => cat !== "");
      if (finalResourceCategories.length === 0) {
        setSubmitError(
          "Please define at least one custom resource category or choose a predefined set."
        );
        setIsSubmitting(false);
        return;
      }
    }

    const gamePayload = {
      description,
      roles: roles.filter((role) => role.name.trim() !== ""),
      rounds: parseInt(rounds, 10),
      steps: parseInt(steps, 10),
      step_time: parseInt(stepTime, 10),
      start_resources: parseInt(startResources, 10),
      extra_resources: parseInt(extraResources, 10),
      max_statements: parseInt(maxStatements, 10),
      resource_categories: finalResourceCategories,
    };

    if (isNaN(gamePayload.max_statements) || gamePayload.max_statements <= 0) {
      setSubmitError("Max Statements must be a positive number.");
      setIsSubmitting(false);
      return;
    }

    console.log("Submitting Game Data:", gamePayload);

    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error("Authentication required.");
      }

      const response = await fetch(`${config.apiUrl}/api/games`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...gamePayload, pagePath }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `HTTP error! status: ${response.status}`
        );
      }

      const result = await response.json();
      console.log("Game created:", result);

      onGameCreated(result);
    } catch (error) {
      console.error("Error creating game:", error);
      setSubmitError(error.message || "An unknown error occurred.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Game management functions
  const handleStartGame = async () => {
    if (!gameId || actionLoading) return;
    setActionLoading(true);
    setActionError(null);
    try {
      const token = await auth.currentUser.getIdToken();
      await axios.post(
        `${config.apiUrl}/api/games/${gameId}/start`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log("Game started successfully via API.");
      if (setFullGameData) {
        setFullGameData((prevData) => ({
          ...prevData,
          current_state: "ACTIVE",
          current_round: 1,
          current_step: 1,
        }));
      }
    } catch (error) {
      console.error("Error starting game:", error);
      setActionError(
        error.response?.data?.error || error.message || "Failed to start game."
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleNextState = async () => {
    if (!gameId || actionLoading) return;
    setActionLoading(true);
    setActionError(null);
    console.log(
      `[handleNextState] Attempting to advance game ${gameId} from state ${currentGameState}`
    );

    try {
      const token = await auth.currentUser.getIdToken();
      console.log(
        `[handleNextState] Sending POST request to /api/games/${gameId}/next`
      );
      const response = await axios.post(
        `${config.apiUrl}/api/games/${gameId}/next`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log("[handleNextState] Received response:", response.data);

      const newState = response.data?.newState;
      console.log("[handleNextState] Extracted newState:", newState);

      if (setFullGameData && newState) {
        console.log(
          "[handleNextState] Calling setFullGameData with new state."
        );
        setFullGameData((prevData) => ({
          ...prevData,
          current_state: newState.state,
          current_round: newState.round,
          current_step: newState.step,
        }));
      } else if (setFullGameData) {
        console.warn(
          "API response for /next missing newState. Cannot update UI immediately."
        );
      }
    } catch (error) {
      console.error("Error advancing game state:", error);
      setActionError(
        error.response?.data?.error ||
          error.message ||
          "Failed to advance game."
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleQualifyRound = async () => {
    if (!gameId || actionLoading || currentGameState !== "ROUND_FEEDBACK")
      return;
    setActionLoading(true);
    setActionError(null);
    setQualificationCompletedForRound(null);
    console.log(
      `[handleQualifyRound] Attempting to finalize round ${currentRound} for game ${gameId}`
    );
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await axios.post(
        `${config.apiUrl}/api/games/${gameId}/qualify`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log("[handleQualifyRound] Received response:", response.data);

      setQualificationCompletedForRound(currentRound);

      const newState = response.data?.newState;
      if (setFullGameData && newState) {
        console.log(
          "[handleQualifyRound] Calling setFullGameData with new state."
        );
        setFullGameData((prevData) => ({
          ...prevData,
          current_state: newState.state,
          current_round: newState.round,
          current_step: newState.step,
        }));
      } else if (setFullGameData) {
        console.warn(
          "API response for /qualify missing newState. Cannot update UI immediately."
        );
      }
    } catch (error) {
      console.error("Error finalizing round:", error);
      setActionError(
        error.response?.data?.error ||
          error.message ||
          "Failed to finalize round."
      );
    } finally {
      setActionLoading(false);
    }
  };

  // Submission labeling functions
  const handleLabelSubmission = async (submissionId, label) => {
    if (
      !gameId ||
      !submissionId ||
      labelingSubmissionId ||
      selectingCategoriesFor?.submissionId === submissionId
    )
      return;

    console.log(`Attempting to label submission ${submissionId} as ${label}`);
    setLabelingError(null);
    setLabelingSuccess(null);
    setActionError(null);

    if (label === "CORRECT") {
      setSelectingCategoriesFor({ submissionId: submissionId, categories: [] });
      setLabelingSubmissionId(null);
    } else {
      setLabelingSubmissionId(submissionId);
      setSelectingCategoriesFor(null);
      try {
        const token = await auth.currentUser.getIdToken();
        await axios.put(
          `${config.apiUrl}/api/games/${gameId}/submissions/${submissionId}/label`,
          { label: label, categories: [] },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log(
          `Submission ${submissionId} labeled successfully as ${label}.`
        );

        // Show success message
        setLabelingSuccess(`Submission labeled as ${label} successfully!`);

        // Clear success message after 3 seconds
        setTimeout(() => {
          setLabelingSuccess(null);
        }, 3000);

        // Update labeling status
        checkLabelingStatus();

        if (setFullGameData) {
          setFullGameData((prevData) => {
            if (!prevData || !prevData.submissions) return prevData;
            return {
              ...prevData,
              submissions: prevData.submissions.map((sub) =>
                sub.id === submissionId ? { ...sub, label: label } : sub
              ),
            };
          });
        }
      } catch (error) {
        console.error(`Error labeling submission ${submissionId}:`, error);
        setLabelingError(
          error.response?.data?.error ||
            error.message ||
            "Failed to label submission."
        );
      } finally {
        setLabelingSubmissionId(null);
      }
    }
  };

  const handleCategorySelectionChange = (categoryName) => {
    if (!selectingCategoriesFor) return;

    const currentSelection = selectingCategoriesFor.categories;
    const newSelection = currentSelection.includes(categoryName)
      ? currentSelection.filter((cat) => cat !== categoryName)
      : [...currentSelection, categoryName];

    setSelectingCategoriesFor({
      ...selectingCategoriesFor,
      categories: newSelection,
    });
  };

  const handleConfirmCorrectLabel = async () => {
    if (!selectingCategoriesFor || labelingSubmissionId) return;

    const { submissionId, categories } = selectingCategoriesFor;

    if (categories.length === 0) {
      setLabelingError(
        "Please select at least one category for a CORRECT submission."
      );
      return;
    }

    console.log(
      `Confirming CORRECT label for submission ${submissionId} with categories:`,
      categories
    );
    setLabelingSubmissionId(submissionId);
    setLabelingError(null);
    setLabelingSuccess(null);

    try {
      const token = await auth.currentUser.getIdToken();
      await axios.put(
        `${config.apiUrl}/api/games/${gameId}/submissions/${submissionId}/label`,
        { label: "CORRECT", categories: categories },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log(
        `Submission ${submissionId} labeled successfully as CORRECT.`
      );

      // Show success message
      setLabelingSuccess("Submission labeled as CORRECT successfully!");

      // Clear success message after 3 seconds
      setTimeout(() => {
        setLabelingSuccess(null);
      }, 3000);

      // Update labeling status
      checkLabelingStatus();

      if (setFullGameData) {
        setFullGameData((prevData) => {
          if (!prevData || !prevData.submissions) return prevData;
          return {
            ...prevData,
            submissions: prevData.submissions.map((sub) =>
              sub.id === submissionId ? { ...sub, label: "CORRECT" } : sub
            ),
          };
        });
      }
      setSelectingCategoriesFor(null);
    } catch (error) {
      console.error(
        `Error labeling submission ${submissionId} as CORRECT:`,
        error
      );
      setLabelingError(
        error.response?.data?.error ||
          error.message ||
          "Failed to label submission."
      );
    } finally {
      setLabelingSubmissionId(null);
    }
  };

  const handleCancelCategorySelection = () => {
    setSelectingCategoriesFor(null);
    setLabelingError(null);
    setLabelingSuccess(null);
  };

  // Format game state display
  const formatGameState = () => {
    switch (currentGameState) {
      case "PRELIMINARY":
        return `Players can now see their roles. Continue with the first submission.`;
      case "LOBBY":
        return "Lobby (Waiting to Start)";
      case "ACTIVE":
        return `Round ${currentRound} / Step ${currentStep}`;
      case "ROUND_FEEDBACK":
        return `Round ${currentRound} / Feedback`;
      case "ENDED":
        return "Game Ended";
      default:
        return currentGameState;
    }
  };

  // Custom resource category helpers
  const handleCustomCategoryChange = (index, value) => {
    const updatedCategories = [...customResourceCategories];
    updatedCategories[index] = value;
    setCustomResourceCategories(updatedCategories);
  };

  const handleAddCustomCategory = () => {
    setCustomResourceCategories([...customResourceCategories, ""]);
  };

  const handleRemoveCustomCategory = (index) => {
    if (customResourceCategories.length <= 1) return;
    const updatedCategories = customResourceCategories.filter(
      (_, i) => i !== index
    );
    setCustomResourceCategories(updatedCategories);
  };

  // Construct QR code URL
  const joinUrl = isManagementMode
    ? `${window.location.origin}/react-app/game/${gameId}`
    : "";

  // Management mode - show game status and controls
  if (isManagementMode) {
    return (
      <div className="game-form">
        <div className="form-content">
          {/* Game Status Section */}
          <div className="game-state-section">
            <h3 className="game-state">{formatGameState()}</h3>
            {actionError && <p className="error">{actionError}</p>}

            <div className="game-controls">
              {currentGameState === "LOBBY" && (
                <button
                  type="button"
                  className="button button-primary"
                  onClick={handleStartGame}
                  disabled={actionLoading || playersLoading}
                >
                  {actionLoading ? "Starting..." : "Start Game"}
                </button>
              )}

              {currentGameState === "ROUND_FEEDBACK" &&
                qualificationCompletedForRound !== currentRound && (
                  <button
                    type="button"
                    className="button button-primary"
                    onClick={handleQualifyRound}
                    disabled={actionLoading}
                  >
                    {actionLoading
                      ? "Finalizing..."
                      : "Finalize Round & Qualify Submissions"}
                  </button>
                )}

              {(currentGameState === "ACTIVE" ||
                currentGameState === "PRELIMINARY" ||
                (currentGameState === "ROUND_FEEDBACK" &&
                  qualificationCompletedForRound === currentRound)) && (
                <button
                  type="button"
                  className="button button-primary"
                  onClick={handleNextState}
                  disabled={
                    actionLoading ||
                    labelingStatus.isChecking ||
                    (currentGameState === "ACTIVE" &&
                      !labelingStatus.allLabeled)
                  }
                  title={
                    currentGameState === "ACTIVE" && !labelingStatus.allLabeled
                      ? `Cannot advance: ${labelingStatus.unlabeledCount} submission(s) need to be labeled`
                      : ""
                  }
                >
                  {actionLoading
                    ? "Advancing..."
                    : currentGameState === "PRELIMINARY"
                    ? "Start the game"
                    : currentGameState === "ROUND_FEEDBACK"
                    ? "End Round / Start Next"
                    : labelingStatus.isChecking
                    ? "Checking..."
                    : "Next Step"}
                </button>
              )}
            </div>

            {currentGameState === "LOBBY" && playersError && (
              <p className="error">
                Could not load player count to verify start conditions.
              </p>
            )}

            {currentGameState === "LOBBY" &&
              !playersLoading &&
              !playersError &&
              playersList.length !== (existingGameData?.roles?.length || 0) && (
                <p className="error">
                  Cannot start: Number of players ({playersList.length}) must
                  match number of roles ({existingGameData?.roles?.length || 0}
                  ).
                </p>
              )}

            {currentGameState === "ACTIVE" && !labelingStatus.allLabeled && (
              <p className="info-message">
                <strong>Note:</strong> {labelingStatus.unlabeledCount}{" "}
                submission(s) need to be labeled before advancing to the
                feedback round.
              </p>
            )}
          </div>

          {/* Submissions Section */}
          {currentGameState !== "LOBBY" && (
            <div className="submissions-section">
              {labelingError && <p className="error">{labelingError}</p>}
              {labelingSuccess && <p className="success">{labelingSuccess}</p>}
              {allSubmissions.length === 0 ? (
                <p className="no-submissions">No submissions yet.</p>
              ) : (
                <div className="submissions-container">
                  <table className="submissions-table">
                    <thead>
                      <tr>
                        <th className="table-cell">Round/Step</th>
                        <th className="table-cell">Submission</th>
                        <th className="table-cell">Label</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allSubmissions.map((sub) => (
                        <tr key={sub.id}>
                          <td className="table-cell">
                            {sub.submitted_in_round}/{sub.submitted_in_step}
                          </td>
                          <td className="table-cell">{sub.text_content}</td>
                          <td className="table-cell">
                            {sub.label ? (
                              <span
                                className={`label-${sub.label.toLowerCase()}`}
                              >
                                {sub.label}
                              </span>
                            ) : selectingCategoriesFor?.submissionId ===
                              sub.id ? (
                              <div className="category-selection">
                                <div className="category-selection-header">
                                  Select Categories:
                                </div>
                                {(
                                  existingGameData?.resource_categories || []
                                ).map((catName) => (
                                  <label
                                    key={catName}
                                    className="category-option"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectingCategoriesFor.categories.includes(
                                        catName
                                      )}
                                      onChange={() =>
                                        handleCategorySelectionChange(catName)
                                      }
                                      disabled={labelingSubmissionId === sub.id}
                                    />
                                    {catName}
                                  </label>
                                ))}
                                <div className="category-selection-buttons">
                                  <button
                                    type="button"
                                    className={`button button-primary ${
                                      labelingSubmissionId === sub.id
                                        ? "disabled"
                                        : ""
                                    }`}
                                    onClick={handleConfirmCorrectLabel}
                                    disabled={
                                      labelingSubmissionId === sub.id ||
                                      selectingCategoriesFor.categories
                                        .length === 0
                                    }
                                  >
                                    {labelingSubmissionId === sub.id
                                      ? "Confirming..."
                                      : "Confirm Correct"}
                                  </button>
                                  <button
                                    type="button"
                                    className="button"
                                    onClick={handleCancelCategorySelection}
                                    disabled={labelingSubmissionId === sub.id}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="label-buttons">
                                <button
                                  type="button"
                                  className="label-button label-button-correct"
                                  onClick={() =>
                                    handleLabelSubmission(sub.id, "CORRECT")
                                  }
                                  disabled={labelingSubmissionId === sub.id}
                                >
                                  {labelingSubmissionId === sub.id
                                    ? "..."
                                    : "✔️"}
                                </button>
                                <button
                                  type="button"
                                  className="label-button label-button-implausible"
                                  onClick={() =>
                                    handleLabelSubmission(sub.id, "IMPLAUSIBLE")
                                  }
                                  disabled={labelingSubmissionId === sub.id}
                                >
                                  {labelingSubmissionId === sub.id
                                    ? "..."
                                    : "❓"}
                                </button>
                                <button
                                  type="button"
                                  className="label-button label-button-illogical"
                                  onClick={() =>
                                    handleLabelSubmission(sub.id, "ILLOGICAL")
                                  }
                                  disabled={labelingSubmissionId === sub.id}
                                >
                                  {labelingSubmissionId === sub.id
                                    ? "..."
                                    : "❌"}
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Players Section */}
          <div className="player-section">
            <div className="game-id-container">
              <span className="game-id-label">Game ID:</span>
              <span className="game-id">{gameId}</span>
              {playersLoading ? (
                <div className="loading-indicator">Loading players...</div>
              ) : (
                <p>Players ({playersList.length})</p>
              )}
            </div>

            {removeError && <p className="error">{removeError}</p>}

            {!playersError &&
              (playersList.length === 0 && !playersLoading ? (
                <p className="no-players">No players have joined yet.</p>
              ) : (
                <div className="player-list-container">
                  <ul className="player-list">
                    {playersList.map((player) => (
                      <li key={player.userId} className="player-item">
                        <span className="player-name">
                          {player.email ||
                            player.displayName ||
                            `User ID: ${player.userId}`}
                        </span>
                        {!isGameActive && (
                          <button
                            type="button"
                            className="button button-danger"
                            onClick={() => handleRemovePlayer(player.userId)}
                            disabled={
                              isGameActive || removingPlayerId === player.userId
                            }
                          >
                            {removingPlayerId === player.userId
                              ? "Removing..."
                              : "Remove"}
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

            {/* QR Code Section */}
            <div className="qr-code-section">
              <button
                type="button"
                className="button button-primary"
                onClick={() => setShowQrCode(!showQrCode)}
              >
                {showQrCode ? "Hide" : "Show"} Join QR Code
              </button>
              {showQrCode && (
                <div className="qr-code-container">
                  <QRCodeSVG value={joinUrl} size={200} includeMargin={true} />
                  <p className="join-url">{joinUrl}</p>
                </div>
              )}
            </div>
          </div>

          {/* Game Settings Display */}
          {isGameActive && (
            <div className="game-settings">
              <div className="settings-grid">
                <div className="setting-item">
                  <span className="setting-value">
                    {existingGameData?.description}
                  </span>
                </div>
                <div className="setting-item">
                  <span className="setting-value">
                    {existingGameData?.roles?.map((r) => r.name).join(", ") ||
                      "N/A"}
                  </span>
                </div>
                <div className="setting-item">
                  <span className="setting-value">
                    {existingGameData?.rounds} rounds
                  </span>
                </div>
                <div className="setting-item">
                  <span className="setting-value">
                    {existingGameData?.steps} steps per round
                  </span>
                </div>
                <div className="setting-item">
                  <span className="setting-value">
                    {existingGameData?.step_time} seconds per step
                  </span>
                </div>
                <div className="setting-item">
                  <span className="setting-value">
                    {existingGameData?.start_resources} resources per category
                  </span>
                </div>
                <div className="setting-item">
                  <span className="setting-value">
                    {existingGameData?.extra_resources} extra Resources per
                    Round
                  </span>
                </div>
                <div className="setting-item">
                  <span className="setting-value">
                    {existingGameData?.max_statements} statements qualify per
                    round
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Creation mode - show creation form
  return (
    <form onSubmit={handleSubmit} className="game-form">
      <div className="form-content">
        <div className="description">
          <textarea
            className="input textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            disabled={isSubmitting}
            placeholder="Scenario"
            aria-label="Game Description"
          />
        </div>

        <div className="role-section">
          <div className="role-header">
            <button
              type="button"
              className="button button-primary"
              onClick={() => setIsRolesCollapsed(!isRolesCollapsed)}
            >
              {isRolesCollapsed
                ? `▶ Show Roles (${roles.length})`
                : `▼ Hide Roles (${roles.length})`}
            </button>
            <button
              type="button"
              className="button button-primary"
              onClick={handleAddRole}
              disabled={isSubmitting}
            >
              Add Role
            </button>
          </div>

          {!isRolesCollapsed && (
            <>
              {roles.map((role, index) => (
                <div key={index} className="role-row">
                  <div className="role-inputs-container">
                    <input
                      type="text"
                      className="input"
                      value={role.name}
                      onChange={(e) =>
                        handleRoleChange(index, "name", e.target.value)
                      }
                      placeholder={`Role ${index + 1} Name`}
                      disabled={isSubmitting}
                    />
                    <button
                      type="button"
                      className="button button-danger"
                      onClick={() => handleRemoveRole(index)}
                      disabled={isSubmitting || roles.length <= 1}
                    >
                      Remove
                    </button>
                  </div>
                  <textarea
                    className="input textarea role-description"
                    value={role.description}
                    onChange={(e) =>
                      handleRoleChange(index, "description", e.target.value)
                    }
                    placeholder={`Role ${index + 1} Goals`}
                    disabled={isSubmitting}
                  />
                </div>
              ))}
            </>
          )}
        </div>

        <div className="game-settings">
          <div className="number-input-container">
            <label className="label">Rounds:</label>
            <input
              type="number"
              className="input number-input"
              value={rounds}
              onChange={(e) => handleNumberChange(setRounds, e.target.value, 1)}
              min="1"
              disabled={isSubmitting}
            />
          </div>
          <div className="number-input-container">
            <label className="label">Steps per Round:</label>
            <input
              type="number"
              className="input number-input"
              value={steps}
              onChange={(e) => handleNumberChange(setSteps, e.target.value, 1)}
              min="1"
              disabled={isSubmitting}
            />
          </div>
          <div className="number-input-container">
            <label className="label">Time per Step (seconds):</label>
            <input
              type="number"
              className="input number-input"
              value={stepTime}
              onChange={(e) =>
                handleNumberChange(setStepTime, e.target.value, 5)
              }
              min="5"
              disabled={isSubmitting}
            />
          </div>
          <div className="number-input-container">
            <label className="label">Starting Resources:</label>
            <input
              type="number"
              className="input number-input"
              value={startResources}
              onChange={(e) =>
                handleNumberChange(setStartResources, e.target.value, 0)
              }
              min="0"
              disabled={isSubmitting}
            />
          </div>
          <div className="number-input-container">
            <label className="label">Extra Resources per Round:</label>
            <input
              type="number"
              className="input number-input"
              value={extraResources}
              onChange={(e) =>
                handleNumberChange(setExtraResources, e.target.value, 0)
              }
              min="0"
              disabled={isSubmitting}
            />
          </div>
          <div className="number-input-container">
            <label className="label">Max Statements per Round:</label>
            <input
              type="number"
              className="input number-input"
              value={maxStatements}
              onChange={(e) =>
                handleNumberChange(setMaxStatements, e.target.value, 1)
              }
              min="1"
              disabled={isSubmitting}
            />
          </div>
        </div>

        <div className="resource-category-options">
          <label
            className={`resource-category-option ${
              resourceCategoryOption === "DIME" ? "checked" : ""
            } ${isSubmitting ? "disabled" : ""}`}
          >
            <input
              type="radio"
              name="resourceCategoryOption"
              value="DIME"
              checked={resourceCategoryOption === "DIME"}
              onChange={(e) => setResourceCategoryOption(e.target.value)}
              disabled={isSubmitting}
            />
            <span>DIME</span>
          </label>
          <label
            className={`resource-category-option ${
              resourceCategoryOption === "PMESII" ? "checked" : ""
            } ${isSubmitting ? "disabled" : ""}`}
          >
            <input
              type="radio"
              name="resourceCategoryOption"
              value="PMESII"
              checked={resourceCategoryOption === "PMESII"}
              onChange={(e) => setResourceCategoryOption(e.target.value)}
              disabled={isSubmitting}
            />
            <span>PMESII</span>
          </label>
          <label
            className={`resource-category-option ${
              resourceCategoryOption === "CUSTOM" ? "checked" : ""
            } ${isSubmitting ? "disabled" : ""}`}
          >
            <input
              type="radio"
              name="resourceCategoryOption"
              value="CUSTOM"
              checked={resourceCategoryOption === "CUSTOM"}
              onChange={(e) => {
                setResourceCategoryOption(e.target.value);
                if (
                  customResourceCategories.length === 0 ||
                  customResourceCategories.every((cat) => cat.trim() === "")
                ) {
                  setCustomResourceCategories([""]);
                }
              }}
              disabled={isSubmitting}
            />
            <span>Custom</span>
          </label>
        </div>

        {resourceCategoryOption === "CUSTOM" && (
          <div className="custom-categories">
            <label className="label">Define Custom Categories:</label>
            {customResourceCategories.map((category, index) => (
              <div key={index} className="custom-category-row">
                <input
                  type="text"
                  className="custom-category-input input"
                  value={category}
                  onChange={(e) =>
                    handleCustomCategoryChange(index, e.target.value)
                  }
                  placeholder={`Category ${index + 1}`}
                  disabled={isSubmitting}
                  aria-label={`Custom Category ${index + 1}`}
                />
                <button
                  type="button"
                  className="button button-danger"
                  onClick={() => handleRemoveCustomCategory(index)}
                  disabled={
                    isSubmitting || customResourceCategories.length <= 1
                  }
                  aria-label={`Remove Custom Category ${index + 1}`}
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              className="button button-primary"
              onClick={handleAddCustomCategory}
              style={{ marginTop: "5px" }}
              disabled={isSubmitting}
            >
              Add Custom Category
            </button>
          </div>
        )}

        {submitError && <p className="error">{submitError}</p>}
      </div>

      <button
        type="submit"
        className="floating-submit-button"
        disabled={isSubmitting}
      >
        {isSubmitting ? "Submitting..." : "Create Game"}
      </button>
    </form>
  );
}

export default GameCreationForm;
